import React, { useRef, useEffect } from 'react';
import { Editor } from '@tinymce/tinymce-react';

/**
 * Reusable TinyMCE Rich Text Editor component.
 * 
 * Props:
 *   value      - current HTML content
 *   onChange   - callback(newHtmlContent)
 *   height     - editor height in px (default: 300)
 *   placeholder - placeholder text
 *   label      - optional label shown above editor
 *   disabled   - disable editing
 */
export default function RichTextEditor({
  value = '',
  onChange,
  height = 450,
  placeholder = 'Tulis konten di sini...',
  label,
  disabled = false,
}) {
  const editorRef = useRef(null);

  // TinyMCE API key (free tier)
  const TINYMCE_API_KEY = 'wrf9c0wbucxjhnl7ctfcwx3yshqzyiuwfp1aipc6q833augs';

  return (
    <div className="rich-text-editor-wrapper">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      )}
      <Editor
        apiKey={TINYMCE_API_KEY}
        onInit={(evt, editor) => { editorRef.current = editor; }}
        value={value}
        onEditorChange={(newValue) => {
          if (onChange) onChange(newValue);
        }}
        init={{
          height,
          menubar: true,
          placeholder,
          disabled,
          plugins: [
            'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',
            'preview', 'anchor', 'searchreplace', 'visualblocks', 'code',
            'fullscreen', 'insertdatetime', 'media', 'table', 'help',
            'wordcount', 'emoticons',
          ],
          toolbar:
            'undo redo | blocks fontfamily fontsize | ' +
            'bold italic underline strikethrough forecolor backcolor | ' +
            'alignleft aligncenter alignright alignjustify | ' +
            'bullist numlist outdent indent | ' +
            'link image media table | ' +
            'removeformat fullscreen preview code | help',
          content_style: `
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
              font-size: 14px;
              line-height: 1.6;
              color: #374151;
              padding: 12px;
            }
            h1, h2, h3, h4, h5, h6 { color: #111827; margin-top: 1em; margin-bottom: 0.5em; }
            p { margin-bottom: 0.75em; }
            ul, ol { padding-left: 1.5em; margin-bottom: 0.75em; }
            a { color: #059669; }
            img { max-width: 100%; height: auto; border-radius: 8px; }
            table { border-collapse: collapse; width: 100%; }
            table td, table th { border: 1px solid #d1d5db; padding: 8px; }
            blockquote {
              border-left: 4px solid #059669;
              padding-left: 16px;
              margin-left: 0;
              color: #6b7280;
              font-style: italic;
            }
          `,
          // Indonesian language
          language: 'id',
          // Image upload disabled (use Media page instead)
          images_upload_handler: (blobInfo) => {
            return new Promise((resolve, reject) => {
              reject('Upload gambar melalui halaman Media terlebih dahulu, lalu paste URL-nya di sini.');
            });
          },
          // Style formats
          style_formats: [
            { title: 'Heading 1', format: 'h1' },
            { title: 'Heading 2', format: 'h2' },
            { title: 'Heading 3', format: 'h3' },
            { title: 'Paragraph', format: 'p' },
            { title: 'Blockquote', format: 'blockquote' },
            { title: 'Code', format: 'code' },
          ],
          // Quick toolbar
          quickbars_selection_toolbar:
            'bold italic underline | quicklink h2 h3 blockquote',
          quickbars_insert_toolbar: false,
          // Context menu
          contextmenu: 'link image table',
          // Auto-save disabled (manual save)
          autosave_ask_before_unload: false,
        }}
      />
    </div>
  );
}